import React, {useCallback, useEffect, useState} from 'react'
import { Link } from 'react-router-dom'
import {auth, db} from '..'
import { ref, get, update, onValue } from 'firebase/database'
import {sendEmailVerification } from 'firebase/auth'
import Loading from './components/Loading'
import TabMenu from './components/TabMenu'
import AddressLabel from './components/AddressLabel'

// 位置情報の精度・エラーメッセージ（コンポーネント外の定数のため依存配列に含める必要がない）
const locationAccuracyText = ['良い', 'やや悪い', '悪い'];
const locationErrorText = ["位置情報の取得が許可されていません", "エラーによって位置情報を取得できませんでした", "位置情報を取得するまでに時間がかかりすぎています"];

const Home = (props) => {
    const [games, setGames] = useState([]);
    const [activatedStatus, setActivatedStatus] = useState({}); // { [gameId]: boolean }（gamesの読込み完了タイミングに依存しないよう分離）
    const [createdGamesData, setCreatedGamesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [location, setLocation] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [menuOpen, setMenuOpen] = useState([]);
    const [createdMenuOpen, setCreatedMenuOpen] = useState([]);
    const [archivedGames, setArchivedGames] = useState([]);
    const [archivedCreatedGames, setArchivedCreatedGames] = useState([]);
    const [tabMenuActive, setTabMenuActive] = useState(0);

    // 位置情報を取得
    const getLocation = useCallback(() => {
        if(navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                setLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy: position.coords.accuracy < 10 ? 0 : (position.coords.accuracy < 50 ? 1 : 2)
                });
            },
            (error) => {
                console.log(error);
                setLocation(null);
                setLocationError(locationErrorText[error.code - 1]);
            });
        }
    }, []);

    // 位置情報を取得（初回マウント時のみ）
    useEffect(() => {
        getLocation();
    }, [getLocation]);

    // チームIDを取得
    const getTeamId = useCallback((members) => {
        let myTeamId = null;

        Object.keys(members).forEach((key) => {
            if(members[key].uid === props.userData.uid) {
                myTeamId = members[key].team;
            }
        });

        return myTeamId;
    }, [props.userData.uid]);

    // 作成したゲームの情報を取得（各ゲームの取得は並行実行して表示を高速化）
    const getCreatedGame = useCallback(async () => {
        if(props.userData.createdGames === undefined) return;

        setCreatedMenuOpen(Array(Object.keys(props.userData.createdGames).length).fill(false));

        await Promise.all(Object.keys(props.userData.createdGames).map(async (key) => {
            const gameId = props.userData.createdGames[key];
            const gameRef = ref(db, 'games/' + gameId);
            const snapshot = await get(gameRef);
            if(snapshot.exists()) {
                setCreatedGamesData((prev) => {
                    const newPrevData = prev.filter((game) => game.id !== gameId);
                    return [
                        ...newPrevData,
                        {
                            id: gameId,
                            ...snapshot.val()
                        }
                    ]
                });
            }
        }));

        setLoading(false);
    }, [props.userData]);

    // 参加したゲームの情報を取得
    const getJoinedGame = useCallback(async () => {
        if(props.userData.joinedGames === undefined) {return;}

        setMenuOpen(Array(props.userData.joinedGames.length).fill(false));

        // ゲームデータを取得（各ゲームの取得は並行実行して表示を高速化）
        await Promise.all(Object.keys(props.userData.joinedGames).map(async (key) => {
            const gameId = props.userData.joinedGames[key];
            const gameRef = ref(db, 'games/' + gameId);

            const snapshot = await get(gameRef);
            if(snapshot.exists()) {
                setGames((prev) => {
                    const newPrevData = prev.filter((game) => game.id !== gameId);
                    return [
                        ...newPrevData,
                        {
                            id: gameId,
                            teamId: getTeamId(snapshot.val().members),
                            ...snapshot.val()
                        }
                    ]
                });
            }
        }));

        setLoading(false);
    }, [props.userData, getTeamId]);

    // 参加ゲームのアクティベート状態をリアルタイムに購読し、
    // 主催者が「アクティベート」した瞬間にプレイ可否表示を自動で切り替える
    useEffect(() => {
        if(!props.userData.joinedGames) return;

        const unsubscribes = Object.keys(props.userData.joinedGames).map((key) => {
            const gameId = props.userData.joinedGames[key];
            const activatedRef = ref(db, 'activatedGames/' + gameId);

            return onValue(activatedRef, (snapshot) => {
                const activated = snapshot.exists() && (snapshot.val().activated || snapshot.val().stoppedDate);
                setActivatedStatus((prev) => ({ ...prev, [gameId]: activated }));
            });
        });

        return () => {
            unsubscribes.forEach((unsubscribe) => unsubscribe());
        };
    }, [props.userData.joinedGames]);

    // ユーザーデータが変更されたらゲーム情報を取得する
    // games.length/createdGamesData.lengthは「まだ読み込んでいなければ取得する」ためのガードとして参照しており、
    // 依存配列に含めると取得中に何度も再実行されてしまうため意図的に除外している
    useEffect(() => {
        if(props.userData) {
            // 参加したゲームの情報を取得
            if(props.userData.joinedGames && games.length === 0) {
                getJoinedGame();
            }

            // 作成したゲームの情報を取得
            if(props.userData.createdGames && createdGamesData.length === 0) {
                getCreatedGame();
            }

            // ローディング終了
            if(!props.userData.loading && !props.userData.joinedGames && !props.userData.createdGames) {
                setLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.userData, getJoinedGame, getCreatedGame]);

    // アーカイブ済みゲーム情報をユーザーデータと同期
    useEffect(() => {
        if(props.userData && props.userData.archivedGames) {
            setArchivedGames(props.userData.archivedGames);
        }
        if(props.userData && props.userData.archivedCreatedGames) {
            setArchivedCreatedGames(props.userData.archivedCreatedGames);
        }
    }, [props.userData]);

    // 外部クリックでメニューを閉じる（マウント時のみ登録）
    useEffect(() => {
        const handleClickOutside = (event) => {
            // メニューボタンまたはメニュー内容がクリックされた場合は何もしない
            if (event.target.closest('.menu-container')) {
                return;
            }
            setMenuOpen(prev => prev.map(() => false));
            setCreatedMenuOpen(prev => prev.map(() => false));
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    // 参加済みのゲームをユーザーデータ内でアーカイブ
    const archiveGame = (gameId) => {
        try {
            // アーカイブ対象のゲーム情報を取得
            const gameToArchive = games.find((game) => game.id === gameId);
            if (!gameToArchive) {
                console.error("Game not found for archiving:", gameId);
                alert("ゲーム情報が見つかりませんでした。");
                return;
            }

            // ゲーム終了時刻をチェック
            const currentTime = new Date();
            const gameEndTime = new Date(gameToArchive.info.endTime);
            
            if (currentTime < gameEndTime) {
                alert("ゲーム終了前はアーカイブできません。\nゲーム終了時刻: " + gameEndTime.toLocaleString());
                return;
            }

            // 現在の参加ゲームリストを安全に配列として取得
            const currentJoinedGames = Array.isArray(props.userData.joinedGames) 
                ? props.userData.joinedGames 
                : (props.userData.joinedGames ? Object.values(props.userData.joinedGames) : []);

            // ユーザーデータの参加するゲームから削除
            const newJoinedGames = currentJoinedGames.filter((id) => {
                return id !== gameId;
            });

            // ユーザーデータのアーカイブ済みゲームに追加
            const archivedGame = {
                gameId: gameId,
                name: gameToArchive.info.name,
            }
            
            // 現在のアーカイブリストを安全に取得
            const currentArchivedGames = Array.isArray(archivedGames) ? archivedGames : [];
            const newArchivedGames = [...currentArchivedGames, archivedGame];

            // ユーザーデータを更新
            const userRef = ref(db, 'users/' + props.userData.uid);
            update(userRef, {
                joinedGames: newJoinedGames,
                archivedGames: newArchivedGames
            }).then(() => {
                console.log("Game archived:", gameId);
                setGames((prev) => prev.filter((game) => game.id !== gameId));
                setArchivedGames(newArchivedGames);
                setMenuOpen([...menuOpen].fill(false));
                alert("ゲームをアーカイブしました。");
            }).catch((error) => {
                console.error("Error archiving game:", error);
                alert("ゲームのアーカイブに失敗しました。もう一度お試しください。");
            });
        } catch (error) {
            console.error("Error in archiveGame:", error);
            alert("予期しないエラーが発生しました。");
        }
    }

    // 作成したゲームをユーザーデータ内でアーカイブ
    const archiveCreatedGame = (gameId) => {
        try {
            // アーカイブ対象のゲーム情報を取得
            const gameToArchive = createdGamesData.find((game) => game.id === gameId);
            if (!gameToArchive) {
                console.error("Created game not found for archiving:", gameId);
                alert("作成ゲーム情報が見つかりませんでした。");
                return;
            }

            // ゲーム終了時刻をチェック
            const currentTime = new Date();
            const gameEndTime = new Date(gameToArchive.info.endTime);
            
            if (currentTime < gameEndTime) {
                alert("ゲーム終了前はアーカイブできません。\nゲーム終了時刻: " + gameEndTime.toLocaleString());
                return;
            }

            // 現在の作成ゲームリストを安全に配列として取得
            const currentCreatedGames = Array.isArray(props.userData.createdGames) 
                ? props.userData.createdGames 
                : (props.userData.createdGames ? Object.values(props.userData.createdGames) : []);

            // ユーザーデータの作成したゲームから削除
            const newCreatedGames = currentCreatedGames.filter((id) => {
                return id !== gameId;
            });

            // ユーザーデータのアーカイブ済み作成ゲームに追加
            const archivedGame = {
                gameId: gameId,
                name: gameToArchive.info.name,
            }
            
            // 現在のアーカイブリストを安全に取得
            const currentArchivedCreatedGames = Array.isArray(archivedCreatedGames) ? archivedCreatedGames : [];
            const newArchivedCreatedGames = [...currentArchivedCreatedGames, archivedGame];

            // ユーザーデータを更新
            const userRef = ref(db, 'users/' + props.userData.uid);
            update(userRef, {
                createdGames: newCreatedGames,
                archivedCreatedGames: newArchivedCreatedGames
            }).then(() => {
                console.log("Created game archived:", gameId);
                setCreatedGamesData((prev) => prev.filter((game) => game.id !== gameId));
                setArchivedCreatedGames(newArchivedCreatedGames);
                setCreatedMenuOpen([...createdMenuOpen].fill(false));
                alert("作成ゲームをアーカイブしました。");
            }).catch((error) => {
                console.error("Error archiving created game:", error);
                alert("作成ゲームのアーカイブに失敗しました。もう一度お試しください。");
            });
        } catch (error) {
            console.error("Error in archiveCreatedGame:", error);
            alert("予期しないエラーが発生しました。");
        }
    }

    // メニューを開く
    const openMenu = (index) => {
        setMenuOpen((prev) => {
            const newPrevData = [...prev];
            newPrevData[index] = !newPrevData[index];
            return newPrevData;
        });
    }

    // メール再送信
    const resendMail = () => {
        sendEmailVerification(auth.currentUser)
        .then(() => {
            console.log('Email sent:', auth.currentUser.email);
            alert("確認メールを送信しました。");
        });
    }


    return (
        <div className='p-3 max-w-5xl mx-auto pt-6 mb-3 relative'>
            <Loading show={loading} text={"ゲーム一覧の読込み中…"} />

            {/* 使い方へのボタン */}
            <div className='absolute top-2 end-2'>
                <a href="https://traffic-tag-docs.web.app" target="_blank" rel="noreferrer" className='text-purple-800 hover:bg-white/50 p-2 rounded-lg text-lg mb-2 inline-block text-center flex items-center'>
                    <div><i className='bi bi-book me-2'></i></div>
                    <div className='text-base'>ヘルプ</div>
                </a>
            </div>
            
            <h1 className='text-3xl text-center mb-6'><i className="bi bi-house me-2"></i>ホーム</h1>

            {
                props.userData.verified === false ? (
                    <div className='bg-red-50 text-red-700 p-3 rounded-md mb-6 border border-red-400 shadow'>
                        <i className='bi bi-exclamation-triangle-fill me-2'></i>メールアドレスの確認が完了していません。メールを確認してください。
                        <button type="button" className='text-red-700 bg-white py-1 px-2 rounded-md border border-red-100 hover:bg-gray-50' onClick={(e) => {resendMail()}}><i className='bi bi-send me-2'></i>再送</button>
                    </div>
                ) : (
                    // ゲームへの参加・作成ボタン 
                    <div className='grid grid-cols-2 md:grid-cols-4 mb-6'>
                        <Link to="/join" className='block rounded-xl bg-purple-100 text-purple-700 text-base m-2 p-3 text-center border border-purple-300 hover:bg-purple-200'>
                            <i className='bi bi-people text-4xl'></i>
                            <h3 className='mt-2'>参加</h3>
                        </Link>

                        <Link to="/watch" className='block rounded-xl bg-purple-100 text-purple-700 text-base m-2 p-3 text-center border border-purple-300 hover:bg-purple-200'>
                            <i className='bi bi-search text-4xl'></i>
                            <h3 className='mt-2'>観戦</h3>
                        </Link>

                        <Link to="/importPack" className='block rounded-xl bg-purple-100 text-purple-700 text-base m-2 p-3 text-center border border-purple-300 hover:bg-purple-200'>
                            <i className='bi bi-sd-card text-4xl'></i>
                            <h3 className='mt-2'>ゲームパックを開封</h3>
                        </Link>

                        <Link to="/edit" state={{gameID: null}} className='block rounded-xl bg-purple-100 text-purple-700 text-base m-2 p-3 text-center border border-purple-300 hover:bg-purple-200'>
                            <i className='bi bi-plus-lg text-4xl'></i>
                            <h3 className='mt-2'>新規作成</h3>
                        </Link>
                    </div>
                )
            }

            
            {/* タブメニュー（参加 / 作成） */}
            <TabMenu options={['あそぶ', 'つくる']} active={tabMenuActive} setActive={(index) => {setTabMenuActive(index)}} />


            {/* 参加したゲームの表示 */}
            {tabMenuActive === 0 && (
                <>
                <div className='flex justify-end'>
                    {/* アーカイブ済みのゲームの表示ボタン */}
                    <Link to="/archive" className='text-gray-500 hover:text-gray-700 text-sm mb-2 inline-block text-center'>
                        <i className='bi bi-archive me-2'></i>アーカイブ
                    </Link>
                </div>

                {!props.userData.joinedGames && (
                    <div className='text-center text-gray-500/60 my-6'>参加登録済みのゲームはありません</div>
                )}

                <div className='md:grid md:grid-cols-2 lg:grid-cols-3 mb-6'>
                    {/* ゲームの選択ページ */}
                    {
                        games && games.sort((a, b) => new Date(a.info.updateDate) - new Date(b.info.updateDate)).map((game, index) => {
                            return (
                                <div className='relative rounded-xl bg-slate-50 shadow-sm p-4 m-2 pb-16' key={index}>
                                    <div className='text-xl tracking-widest mb-1'>{game.info.name}</div>
                                    <div className='text-base text-gray-500 ms-2'><i className='bi bi-clock me-2'></i>{new Date(game.info.startTime).toLocaleString()}～{new Date(game.info.endTime).toLocaleTimeString()}</div>
                                    {
                                        (game.teamId === null || game.teamId === undefined) ? (
                                            <>
                                                <div className='text-red-500 mb-2 ms-2'><i className='bi bi-exclamation-triangle me-2'></i>所属チームが設定されていません</div>
                                                <button type="button" className="absolute bottom-4 start-4 end-4 p-2 bg-purple-500/50 text-white rounded-md tracking-widest" disabled><i className="bi bi-door-open me-2"></i>プレイ</button>
                                            </>
                                        ) : (
                                            <>
                                                <div className='text-gray-500 mb-2 ms-2'><i className='bi bi-people-fill me-2'></i>{game.teams[game.teamId].name}</div>

                                                {
                                                    (activatedStatus[game.id]) ? (
                                                        <Link to="/game" state={{gameId: game.id, teamId: Number(game.teamId)}} className='absolute bottom-4 start-4 end-4'>
                                                            <button type="button" className="w-full p-2 bg-purple-500 text-white rounded-md hover:bg-purple-700 tracking-widest"><i className="bi bi-door-open me-2"></i>プレイ</button>
                                                        </Link>
                                                    ) : (
                                                        <>
                                                            <span className='text-red-600 mb-1 text-sm'>ゲームはアクティベートされていません</span>
                                                            <button type="button" className="absolute bottom-4 start-4 end-4 p-2 bg-purple-500/50 text-white rounded-md tracking-widest" disabled><i className="bi bi-door-open me-2"></i>プレイ</button>
                                                        </>
                                                    )
                                                }
                                                
                                            </>
                                        )
                                    }
                                    

                                    {/* 三点リーダーメニュー */}
                                    <div className='absolute top-2 end-2 menu-container'>
                                        <button type="button" className="text-gray-500 hover:text-gray-700 p-1" onClick={(e) => {
                                            e.stopPropagation();
                                            openMenu(index);
                                        }}>
                                            <i className='bi bi-three-dots-vertical'></i>
                                        </button>
                                        <ul className={"bg-white rounded-md shadow z-10 text-gray-700 absolute top-6 end-0 min-w-32 " + (menuOpen[index] ? "block" : "hidden")}>
                                            <li><button type="button" className='dropdown-item p-2 hover:bg-gray-100 w-full text-start' onClick={(e) => {
                                                e.stopPropagation();
                                                archiveGame(game.id);
                                            }}><i className='bi bi-archive me-2'></i>アーカイブ</button></li>
                                            <li><button type="button" className='dropdown-item p-2 hover:bg-gray-100 w-full text-start' onClick={(e) => {
                                                e.stopPropagation();
                                                openMenu(index);
                                            }}><i className='bi bi-x-lg me-2'></i>とじる</button></li>
                                        </ul>
                                    </div>
                                </div>
                            )
                        })
                    }
                </div>
                </>
            )}


            {/* 作成したゲームの表示 */}
            {tabMenuActive === 1 && (
                <>
                <div className='flex justify-end'>
                    {/* アーカイブ済みの作成ゲームの表示ボタン */}
                    <Link to="/archive" className='text-gray-500 hover:text-gray-700 text-sm mb-2 inline-block text-center'>
                        <i className='bi bi-archive me-2'></i>アーカイブ
                    </Link>
                </div>

                {!props.userData.createdGames && (
                    <div className='text-center text-gray-500/60 my-6'>作成したゲームはありません</div>
                )}

                <div className='md:grid md:grid-cols-2 mb-6'>
                    {/* 作成したゲーム */}
                    {
                        createdGamesData && createdGamesData.sort((a, b) => new Date(a.info.updateDate) - new Date(b.info.updateDate)).map((game, index) => {
                            return (
                                <div className='rounded-xl shadow-sm bg-slate-50 p-4 m-2 border relative' key={index}>
                                    {/* 3点メニュー */}
                                    <div className="absolute top-3 right-3 menu-container">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setCreatedMenuOpen(prev => {
                                                    const newState = [...prev];
                                                    newState[index] = !newState[index];
                                                    return newState;
                                                });
                                            }}
                                            className="text-gray-500 hover:text-gray-700 p-1"
                                        >
                                            <i className="bi bi-three-dots-vertical"></i>
                                        </button>
                                        {createdMenuOpen[index] && (
                                            <div className="absolute right-0 mt-2 w-32 bg-white rounded-md shadow-lg z-10 border">
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        archiveCreatedGame(game.id);
                                                    }}
                                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                >
                                                    アーカイブ
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className='text-xl tracking-widest'>{game.info.name}</div>
                                    <div className='text-gray-600 text-sm mb-1'>ID: {game.id}</div>
                                    <div className='text-base text-gray-500 ms-2 mb-2'>{new Date(game.info.startTime).toLocaleString()}～{new Date(game.info.endTime).toLocaleTimeString()}</div>
                                    
                                    <div className='grid grid-cols-2'>
                                        <Link to="/control" state={{gameId: game.id}} className='mx-2'>
                                            <button type="button" className="w-full p-2 bg-purple-500 text-white rounded-md hover:bg-purple-700 tracking-widest"><i className="bi bi-flag me-2"></i>コントローラ</button>
                                        </Link>
                                        <Link to="/edit" state={{gameID: game.id}} className='mx-2'>
                                            <button type="button" className="w-full p-2 bg-white text-gray-700 rounded-md hover:bg-gray-100 tracking-widest border"><i className="bi bi-gear me-2"></i>編集</button>
                                        </Link>
                                    </div>
                                </div>
                            )
                        })
                    }
                </div>
                </>
            )}

            <div className='text-center text-gray-500 my-2 text-sm mt-4'>
                {
                    location === null ? (
                        <button type="button" className='bg-white/50 text-red-600 p-3 rounded-md border border-red-400 shadow-sm hover:bg-red-50' onClick={() => getLocation()}>
                            <h5 className='text-base mb-1'><i className='bi bi-exclamation-triangle-fill me-2 text-red-600'></i>位置情報を利用できません<br /></h5>
                            <p className='text-gray-600'>{locationError}</p>
                            <p>クリックして再試行</p>
                        </button>
                    ) : (
                        <p>
                            <i className='bi bi-check-circle-fill me-2 text-lime-600'></i>位置情報を利用可能<br />
                            <AddressLabel lat={location.lat} lng={location.lng} /><br />
                            精度: {locationAccuracyText[location.accuracy]}
                        </p>
                    )
                }
            </div>
        </div>
  )
}

export default Home